import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { isOnSiteRoomSession } from '@streem/sdk-core';
import { AppIcon, LaserAndMarker, Row, Snackbar, Tooltip, styled, useTheme, } from '@streem/ui-react';
import { useRoomSession } from '../../../../context/room_session';
import { useObservable } from '../../../../hooks/rxjs';
import { StreemshotButton } from './streemshot_button';
import { useVideoEnabled } from '../../../../hooks/use_video_enabled';
import { useVideoMuted } from '../../../../hooks/use_video_muted';
import { useIsBackgrounded } from '../../../../hooks/use_is_backgrounded';
import { scan } from 'rxjs';
import { hasScreenSubject, hasWorldSubject } from '../../../../hooks/use_camera_subject';
import { TooltipMessage } from './toolbar_tooltip_message';
function getSelectedTool(tooltype) {
    if (tooltype === 'MARKER') {
        return 'MARKER';
    }
    return 'LASER';
}
export const CallTools = ({ isTrayOpen, remoteUserName, setShowArrow, roundLeftEdgeYn, roundRightEdgeYn, }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const roomSession = useRoomSession();
    const [chosenTool] = useObservable(roomSession.tools.activeTool);
    const [snackbarNotifications, setSnackbarNotifications] = useState([]);
    const [toastTimeout, setToastTimeout] = useState({});
    const remoteVideoController = roomSession.media.videoControllers.pipe(scan((acc, controllers) => {
        return controllers.find(controller => !controller.ownerIsLocalUser) || acc;
    }, undefined));
    const [remoteVideoEnabled] = useVideoEnabled(remoteVideoController);
    const [remoteVideoMuted] = useVideoMuted(remoteVideoController);
    const [isBackgrounded] = useIsBackgrounded(remoteVideoController);
    const isWorld = hasWorldSubject(roomSession.media.videoControllers);
    const isScreen = hasScreenSubject(roomSession.media.videoControllers);
    const faceToFace = !isWorld && !isScreen;
    const theme = useTheme();
    // Disable tools in faceMode mode, if the remote video has not been enabled or if the remote video is backgrounded
    const disabled = isOnSiteRoomSession(roomSession)
        ? faceToFace
        : faceToFace || !remoteVideoEnabled || remoteVideoMuted || isBackgrounded;
    const addNotification = (id) => {
        const TIMEOUT = 2000;
        setSnackbarNotifications([
            {
                id,
                message: 'Streemshot captured!',
                timeout: TIMEOUT,
                icon: _jsx(AppIcon, { color: theme.colors.green50, name: "DoneIcon" }),
            },
        ]);
        removeNotificationViaTimeout(id, TIMEOUT);
    };
    const removeNotificationViaTimeout = (id, timeout) => {
        if (toastTimeout && toastTimeout[id]) {
            window.clearTimeout(toastTimeout[id]);
        }
        const remove = window.setTimeout(() => removeNotification(id), timeout);
        setToastTimeout(prevToastTimeout => ({ ...prevToastTimeout, [id]: remove }));
    };
    const removeNotification = (id) => {
        setSnackbarNotifications(prevSnackbarNotifications => {
            return prevSnackbarNotifications.filter(notification => notification.id !== id);
        });
    };
    useEffect(() => {
        if (disabled) {
            roomSession.tools.setActiveTool('LASER');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);
    const onSelectLaser = () => {
        roomSession.tools.setActiveTool('LASER');
    };
    const onSelectMarker = () => {
        roomSession.tools.setActiveTool('MARKER');
    };
    const name = remoteUserName ? remoteUserName + "'s" : 'their';
    const faceToFaceMessage = (_jsxs(TooltipWrapper, { children: [_jsx(TooltipMessage, { text: "Flip" }), _jsx("span", { style: { margin: '0 5px 0 5px' }, children: _jsx(AppIcon, { name: "FlipIcon", size: "small", color: theme.colors.grey80 }) }), _jsx(TooltipMessage, { text: `${name} camera to enable tools.` })] }));
    const noRemoteVideoMessage = (_jsx(TooltipWrapper, { children: _jsx(TooltipMessage, { text: "Turn on camera to enable tools." }) }));
    const remoteVideoBackgroundedMessage = (_jsx(TooltipWrapper, { children: _jsx(TooltipMessage, { text: "Tools disabled while backgrounded." }) }));
    const show = () => {
        setShowTooltip(true);
        if (setShowArrow) {
            setShowArrow(true);
        }
    };
    const hide = () => {
        setShowTooltip(false);
        if (setShowArrow) {
            setShowArrow(false);
        }
    };
    return (_jsx(Tooltip, { message: faceToFace
            ? faceToFaceMessage
            : isBackgrounded
                ? remoteVideoBackgroundedMessage
                : noRemoteVideoMessage, showTooltip: disabled ? showTooltip : false, borderRadius: "10px", arrowSize: "20px", distance: 30, children: _jsxs(CallToolsWrapper, { onMouseEnter: show, onMouseOver: show, onMouseOut: hide, onFocus: show, onBlur: hide, children: [_jsx(StreemshotButton, { disabled: !!disabled, addNotification: addNotification, roundLeftEdgeYn: roundLeftEdgeYn, roundRightEdgeYn: false }), _jsx(LaserAndMarker, { roundRightEdgeYn: roundRightEdgeYn, roundLeftEdgeYn: false, disabled: !!disabled, selectedTool: getSelectedTool(chosenTool), onSelectLaser: onSelectLaser, onSelectMarker: onSelectMarker }), _jsx(Snackbar, { "data-testid": "streemshot-snackbar-message", content: snackbarNotifications, isTrayOpen: isTrayOpen, position: { vertical: 'top', horizontal: 'center' } })] }) }));
};
const TooltipWrapper = styled(Row)(({ theme }) => ({
    alignItems: 'center',
    padding: theme.spacing.s,
}));
const CallToolsWrapper = styled.span({
    display: 'inline-flex',
    alignItems: 'center',
});
//# sourceMappingURL=call_tools.js.map